/*
	Handles New and Edit matter functionality.  props.editMode determines which.
*/

import React, { Component } from "react";
import { connect } from "react-redux";
import { Navigate } from "react-router-dom";

import {
	getEverythingForEditTenant,
	getEverythingForNewTenant,
	editTenant,
	createTenant,
	unmountNewEditTenant,
	checkIfTenantExists,
} from "../actions/adminActions";
import FlexFormWrapper from "./FlexFormWrapper";
import { LabeledTextInput } from "./TextInput";
import { LabeledTextInputWithValidation } from "./TextFieldWithValidation";

import { LabeledDropDownSelect } from "./DropDownSelect";
import { SimpleSwitch } from "./AsyncSwitch";
import { Divider } from "@mantine/core";

import {
	humanFileSize,
	numberWithCommas,
	shortDateFormat,
} from "../utilities/utilities";

import lxwConstants from "../config/lexworkplace-constants";
import { IconBriefcase } from "@tabler/icons-react";
import { isTenantSuspended } from "./Admin/firmStatusPill";
import { SuspendedFirmMessage } from "./Utils/UnderSubHeaderWarningMessages";
import { parseAndReturnDataUsageString } from "./Homepage/StorageDetails";

const ADVANCED_PREVIEW_DURATION_DAYS = 90;
const lexWorkplaceEditions = lxwConstants.lexWorkplaceEditions;

const initialState = {
	name: "",
	tenantID: null,
	customerID: "",
	usernamePrefix: "",
};

const regions = {
	US: "U.S.",
	CA: "Canada",
};

const makeDataUsageMenuOption = (gigabytes) => {
	const limitInBytes = gigabytes * 1000 * 1000 * 1000;
	return { label: humanFileSize(limitInBytes, true), value: gigabytes };
};
const makeOCRPerMonthBaselineMenuOption = (ocrOperationsPerMonth) => {
	return {
		label: numberWithCommas(ocrOperationsPerMonth),
		value: ocrOperationsPerMonth,
	};
};

const makeRegionMenuOption = (regionName) => {
	if (!regionName) {
		return false;
	}
	return {
		label: regions[regionName],
		value: regionName,
	};
};
const lxwRegionOptions = (() => {
	return ["US", "CA"].map(makeRegionMenuOption);
})();
const standardStorageLimitOptions = (() => {
	var out = [];
	for (let gigabytes = 500; gigabytes <= 20000; gigabytes += 100) {
		out.push(makeDataUsageMenuOption(gigabytes));
	}
	return out;
})();
const archiveStorageLimitOptions = (() => {
	var out = [];
	// add the default first
	out.push(makeDataUsageMenuOption(100));
	for (let gigabytes = 500; gigabytes <= 40000; gigabytes += 500) {
		out.push(makeDataUsageMenuOption(gigabytes));
	}
	return out;
})();

const ocrPerMonthOptions = (() => {
	var out = [];

	for (let val = 2500; val <= 50000; val += 2500) {
		out.push(makeOCRPerMonthBaselineMenuOption(val));
	}
	return out;
})();

const lxwEditionOptions = (() => {
	return Object.keys(lexWorkplaceEditions.options).map((editionCode) => {
		return {
			label: lexWorkplaceEditions.options[editionCode].display,
			value: editionCode,
		};
	});
})();

const makeEditionMenuOption = (editionCode) => {
	return lexWorkplaceEditions.options[editionCode]
		? {
				label: lexWorkplaceEditions.options[editionCode].display,
				value: editionCode,
			}
		: { label: "Unknown", value: "unknown" };
};

class NewTenant extends Component {
	constructor(props) {
		super(props);
		this.state = {
			name: "",
			regionName: "",
			customerID: "",
			usernamePrefix: "",
			tenantID: null,
			loading: true,
			firmNameIsValid: false,
			tenantIDisValid: false,
			usernamePrefixIsValid: false,
		};
		this.handleChange = this.handleChange.bind(this);
		this.canSubmit = this.canSubmit.bind(this);
	}
	static getDerivedStateFromProps(props, state) {
		if (state.loading && !props.loading) {
			const newState = props.editMode
				? {
						loading: false,
						name: props.firmDetails.name,
						regionName: makeRegionMenuOption(props.tenantRegion),
						customerID: props.firmDetails.customerID,
						usernamePrefix: props.firmDetails.usernamePrefix,
						tenantID: props.firmDetails.id,
						storageLimitGB: makeDataUsageMenuOption(
							props.usageLimits.STORAGE_LIMIT_GB
						),
						archiveStorageLimitGB: makeDataUsageMenuOption(
							props.usageLimits.ARCHIVE_STORAGE_LIMIT_GB
						),
						lxwEdition: makeEditionMenuOption(
							props.firmDetails.lxwEdition
						),
						ocrEnabled: props.tenantFeatures.automaticOCR,
						ocrPerMonthBaseline: makeOCRPerMonthBaselineMenuOption(
							props.usageLimits.OCR_PER_MONTH_BASELINE
						),
						literaDocumentCompareEnabled:
							props.tenantFeatures.literaDocumentCompareEnabled,
						inlineDocumentCompareEnabled:
							props.tenantFeatures.inlineDocumentCompareEnabled,
						mfaIsRequiredForAllUsers:
							props.tenantFeatures.mfaIsRequiredForAllUsers,
						archiveEnabled: props.tenantFeatures.archiveEnabled,
						startAdvancedPreviewOnSubmit: false,
						cancelAdvancedPreviewOnSubmit: false,
						editionAfterPreviewExpires: props.activeEditionPreview
							? makeEditionMenuOption(
									props.activeEditionPreview
										.editionAfterPreviewExpires
								)
							: null,
					}
				: {
						...initialState,
						tenantID: props.firmDetails
							? props.firmDetails.id
							: 420,
						regionName: "",
						loading: false,
						storageLimitGB: makeDataUsageMenuOption(
							props.newFirmDefaultStorageLimitGB
						),
						archiveStorageLimitGB: makeDataUsageMenuOption(
							props.newFirmDefaultArchiveStorageLimitGB
						),
						lxwEdition: makeEditionMenuOption(
							props.newFirmEditionCode
						),
						ocrEnabled: props.newFirmDefaultOCREnabled,
						ocrPerMonthBaseline: makeOCRPerMonthBaselineMenuOption(
							props.newFirmDefaultOCRPerMonthBaseline
						),
						literaDocumentCompareEnabled:
							props.newFirmDefaultLiteraDocumentCompare,
						inlineDocumentCompareEnabled:
							props.newFirmDefaultInlineDocumentCompare,
						mfaIsRequiredForAllUsers:
							props.newFirmDefaultMfaIsRequiredForAllUsers,
						archiveEnabled: props.newFirmDefaultArchiveEnabled,
					};
			return newState;
		} else {
			return null;
		}
	}
	componentDidMount() {
		if (this.props.editMode) {
			this.props.getEverythingForEditTenant(
				this.props.match.params.tenantID
			);
		} else {
			this.props.getEverythingForNewTenant();
		}
	}
	componentWillUnmount() {
		this.props.unmountNewEditTenant();
	}
	handleChange(key, value) {
		this.setState({ [key]: value });
	}
	submit() {
		if (this.props.editMode) {
			const manualTenantID = this.props.match.params.tenantID;
			this.props.editTenant({
				manualTenantID,
				name: this.state.name,
				customerID: this.state.customerID,
				storageLimitGB: this.state.storageLimitGB.value,
				archiveStorageLimitGB: this.state.archiveStorageLimitGB.value,
				lxwEdition: this.state.lxwEdition.value,
				ocrEnabled: this.state.ocrEnabled,
				ocrPerMonthBaseline: this.state.ocrPerMonthBaseline.value,
				literaDocumentCompareEnabled:
					this.state.literaDocumentCompareEnabled,
				inlineDocumentCompareEnabled:
					this.state.inlineDocumentCompareEnabled,
				mfaIsRequiredForAllUsers: this.state.mfaIsRequiredForAllUsers,
				archiveEnabled: this.state.archiveEnabled,
				// validation for whether to start advanced preview is done in the backend
				startAdvancedPreviewFlag:
					this.state.startAdvancedPreviewOnSubmit,
				editionAfterPreviewExpires:
					this.state.editionAfterPreviewExpires?.value,
				cancelAdvancedPreviewFlag:
					this.state.cancelAdvancedPreviewOnSubmit,
			});
		} else {
			this.props.createTenant({
				manualTenantID: this.state.tenantID,
				name: this.state.name,
				regionName: this.state.regionName.value,
				customerID: this.state.customerID,
				usernamePrefix: this.state.usernamePrefix,
				storageLimitGB: this.state.storageLimitGB.value,
				archiveStorageLimitGB: this.state.archiveStorageLimitGB.value,
				lxwEdition: this.state.lxwEdition.value,
				ocrEnabled: this.state.ocrEnabled,
				ocrPerMonthBaseline: this.state.ocrPerMonthBaseline.value,
				literaDocumentCompareEnabled:
					this.state.literaDocumentCompareEnabled,
				inlineDocumentCompareEnabled:
					this.state.inlineDocumentCompareEnabled,
				mfaIsRequiredForAllUsers: this.state.mfaIsRequiredForAllUsers,
				archiveEnabled: this.state.archiveEnabled,
			});
		}
	}
	isSuspended() {
		return (
			this.props.editMode &&
			isTenantSuspended(this.props.firmDetails.planType)
		);
	}
	canSubmit() {
		return (
			!this.isSuspended() &&
			this.state.name !== "" &&
			this.state.firmNameIsValid &&
			this.state.customerID !== "" &&
			this.state.tenantIDisValid &&
			this.state.usernamePrefix !== "" &&
			this.state.usernamePrefixIsValid &&
			!!this.state.regionName
		);
	}
	getSubmitDisabledText() {
		if (this.isSuspended()) {
			return "This firm is suspended";
		}
		let errorArray = [];
		if (this.state.name === "") {
			errorArray.push("Firm Name is required");
		}
		if (!this.state.firmNameIsValid) {
			errorArray.push("Firm Name is invalid");
		}
		if (this.state.customerID === "") {
			errorArray.push("Customer ID is required");
		}
		if (!this.state.tenantIDisValid) {
			errorArray.push("Tenant ID is invalid");
		}
		if (this.state.usernamePrefix === "") {
			errorArray.push("Username Prefix is required");
		}
		if (!this.state.regionName) {
			errorArray.push("Region is required");
		}
		if (errorArray.length) {
			return errorArray.join("\n");
		}
		return false;
	}
	// archive cannot be turned off while it is saved as on and the firm still has archived items
	// uses the saved value (tenantFeatures), not this.state, so turning archive on does not lock the switch before saving
	isArchiveLockedOnByArchivedItems() {
		return (
			this.props.editMode &&
			this.props.tenantFeatures.archiveEnabled === 1 &&
			this.props.archivedItemsInfo &&
			this.props.archivedItemsInfo.totalArchivedItems > 0
		);
	}
	determineIfArchiveSwitchIsDisabled() {
		if (this.isSuspended()) {
			return true;
		} else if (this.isArchiveLockedOnByArchivedItems()) {
			return true;
		}
		return false;
	}
	getArchiveSwitchDisabledText() {
		if (this.isArchiveLockedOnByArchivedItems()) {
			let disabledMessage =
				"Archive cannot be turned off until all items are Unarchived. Item Counts:";
			for (const [key, value] of Object.entries(
				this.props.archivedItemsInfo.counts
			)) {
				if (value > 0) {
					disabledMessage += `\n${key}: ${value}`;
				}
			}
			return disabledMessage;
		}
		return "";
	}
	render() {
		if (this.props.navigateAwayFromCreatePage) {
			return (
				<Navigate
					to={
						this.props.editMode
							? "/admin"
							: `/admin/tenant/${this.state.tenantID}/users`
					}
					replace
				/>
			);
		}
		if (this.state.loading) {
			return <div />;
		} else {
			const isSuspended = this.isSuspended();
			return (
				<>
					{isSuspended ? SuspendedFirmMessage() : null}
					<FlexFormWrapper
						headingText="Firm Information"
						rowGap={14}
						iconComponent={
							<IconBriefcase
								size={16}
								style={{
									verticalAlign: "text-top",
								}}
							/>
						}
						canSubmit={this.canSubmit()}
						submitDisabledHoverText={this.getSubmitDisabledText()}
						submit={this.submit.bind(this)}
						submitButtonText={
							this.props.editMode ? "Update Firm" : "Create Firm"
						}
						rows={[
							[
								<LabeledTextInputWithValidation
									key="name"
									label="Firm Name"
									name="name"
									placeholder="New Firm Name"
									disabled={isSuspended}
									defaultValue={
										this.props.editMode
											? this.props.firmDetails.name
											: null
									}
									ignoreMinHeightForValidationMessage={true}
									onChange={this.handleChange}
									autocomplete="off"
									syncValidationError={(enteredFirmName) => {
										if (
											enteredFirmName === "" ||
											enteredFirmName.match(/^\s/) ||
											enteredFirmName.match(/\s$/)
										) {
											return "Firm Name cannot start or end with whitespace";
										}
										return false;
									}}
									fieldIsValidAndReadyChanged={(
										firmNameIsValid
									) => {
										this.setState({
											firmNameIsValid,
										});
									}}
								/>,
								<div key="tenantID">
									{this.props
										.failedCreateAttemptDuplicateID && (
										<div style={{ color: "red" }}>
											ERROR! this tenantID is already in
											use by a different firm
										</div>
									)}
									<LabeledTextInputWithValidation
										label={
											this.props.editMode
												? "Tenant ID"
												: "Tenant ID (auto populated with next available)"
										}
										name="tenantID"
										placeholder="New Tenant ID"
										disabled={this.props.editMode}
										ignoreMinHeightForValidationMessage={
											true
										}
										defaultValue={this.props.firmDetails.id}
										onChange={this.handleChange}
										autocomplete="off"
										syncValidationError={(
											enteredTenantID
										) => {
											if (
												!enteredTenantID.match(/^\d+$/)
											) {
												return "tenantID must be a number";
											}
											return false;
										}}
										asyncValidationError={(newValue) => {
											return checkIfTenantExists(
												newValue
											).then((res) => {
												const { tenantIDinUse } =
													res.mysql;
												if (tenantIDinUse) {
													return "This tenantID is already in use";
												}
												//returning false means no error
												return false;
											});
										}}
										fieldIsValidAndReadyChanged={(
											tenantIDisValid
										) => {
											this.setState({
												tenantIDisValid,
											});
										}}
									/>
								</div>,
							],
							[
								<LabeledDropDownSelect
									key="regionName"
									label="Region"
									name="regionName"
									placeholder="Please select a region"
									isDisabled={this.props.editMode}
									isAsync
									options={lxwRegionOptions}
									defaultValue={
										this.props.editMode
											? [
													makeRegionMenuOption(
														this.props.tenantRegion
													),
												]
											: false
									}
									handler={this.handleChange}
								/>,
								<LabeledTextInputWithValidation
									key="usernamePrefix"
									label={
										this.props.editMode
											? "Username Prefix"
											: "Username Prefix (use firm email domain)"
									}
									name="usernamePrefix"
									placeholder="firm email domain"
									disabled={this.props.editMode}
									defaultValue={
										this.props.editMode
											? this.props.firmDetails
													.usernamePrefix
											: null
									}
									ignoreMinHeightForValidationMessage={true}
									onChange={this.handleChange}
									autocomplete="off"
									syncValidationError={(
										enterUsernamePrefix
									) => {
										if (
											enterUsernamePrefix === "" ||
											enterUsernamePrefix.match(/\s/)
										) {
											return "Username Prefix cannot include whitespace";
										}
										return false;
									}}
									fieldIsValidAndReadyChanged={(
										usernamePrefixIsValid
									) => {
										this.setState({
											usernamePrefixIsValid,
										});
									}}
								/>,
							],
							[
								<LabeledTextInput
									key="customerID"
									label="Internal Customer ID"
									name="customerID"
									placeholder="Customer ID"
									autocomplete="off"
									disabled={isSuspended}
									defaultValue={
										this.props.editMode
											? this.props.firmDetails.customerID
											: null
									}
									onChange={this.handleChange}
								/>,
								<></>,
							],
						]}
						section2={
							<div
								style={{
									display: "flex",
									flexDirection: "row",
								}}
							>
								<div
									style={{
										width: "45vw",
										paddingLeft: "30px",
										paddingRight: "30px",
									}}
								>
									<h4>Tenant Features</h4>
									{this.props.editMode &&
										!this.props.activeEditionPreview &&
										this.state.lxwEdition.value !==
											"ADVANCED" &&
										this.state.lxwEdition.value !==
											"TRIAL" && (
											<div
												style={{
													marginTop: 16,
													marginBottom: 16,
												}}
											>
												<SimpleSwitch
													label="Start Advanced Preview"
													labelVariant="header-5"
													disabled={isSuspended}
													switchValue={
														this.state
															.startAdvancedPreviewOnSubmit ||
														false
													}
													handleSwitchValueChange={() =>
														this.handleChange(
															"startAdvancedPreviewOnSubmit",
															!this.state
																.startAdvancedPreviewOnSubmit
														)
													}
												/>
												{this.state
													.startAdvancedPreviewOnSubmit && (
													<div
														style={{
															marginTop: 8,
															marginBottom: 8,
															color: "var(--lw-dark-grey)",
														}}
													>
														Advanced Preview will be
														activated after
														submitting and will
														expire on{" "}
														{shortDateFormat(
															new Date(
																Date.now() +
																	ADVANCED_PREVIEW_DURATION_DAYS *
																		24 *
																		60 *
																		60 *
																		1000
															)
														)}
														.
													</div>
												)}
												<Divider />
											</div>
										)}
									{this.props.editMode &&
										this.props.activeEditionPreview && (
											<div
												style={{
													backgroundColor:
														"var(--lw-light-blue)",
													border: "1px solid var(--lw-blue)",
													borderRadius: 4,
													padding: 10,
													marginBottom: 10,
												}}
											>
												<div
													style={{
														fontFamily:
															"MainFont-SemiBold",
														marginBottom: 4,
													}}
												>
													In Advanced Preview
												</div>
												<div>
													Expires{" "}
													{shortDateFormat(
														this.props
															.activeEditionPreview
															.expirationDate
													)}{" "}
													(
													{Math.max(
														0,
														Math.ceil(
															(new Date(
																this.props
																	.activeEditionPreview
																	.expirationDate
															).getTime() -
																Date.now()) /
																(1000 *
																	60 *
																	60 *
																	24)
														)
													)}{" "}
													days)
												</div>
												<div style={{ marginTop: 8 }}>
													<SimpleSwitch
														label="Cancel Advanced Preview"
														labelVariant="header-5"
														disabled={isSuspended}
														switchValue={
															this.state
																.cancelAdvancedPreviewOnSubmit ||
															false
														}
														handleSwitchValueChange={() =>
															this.handleChange(
																"cancelAdvancedPreviewOnSubmit",
																!this.state
																	.cancelAdvancedPreviewOnSubmit
															)
														}
													/>
												</div>
												{this.state
													.cancelAdvancedPreviewOnSubmit && (
													<div
														style={{
															marginTop: 8,
														}}
													>
														The Advanced Preview
														will be cancelled and no
														further milestone emails
														will be sent to firm
														admins. The firm will be
														transitioned to the{" "}
														{
															this.state
																.editionAfterPreviewExpires
																?.value
														}{" "}
														edition with default
														tenant features for that
														edition.
													</div>
												)}
											</div>
										)}
									<LabeledDropDownSelect
										key="lxwEdition"
										label="Edition"
										name="lxwEdition"
										placeholder="Edition"
										isAsync
										isDisabled={
											(this.props.editMode &&
												!!this.props
													.activeEditionPreview) ||
											isSuspended
										}
										options={lxwEditionOptions}
										defaultValue={[
											makeEditionMenuOption(
												this.props.editMode
													? this.props.firmDetails
															.lxwEdition
													: this.props
															.newFirmEditionCode
											),
										]}
										handler={this.handleChange}
									/>
									{this.props.editMode &&
										this.props.activeEditionPreview && (
											<div style={{ marginTop: 12 }}>
												<LabeledDropDownSelect
													key="editionAfterPreviewExpires"
													label="Edition After Preview Expires"
													name="editionAfterPreviewExpires"
													placeholder="Edition after preview"
													isAsync
													isDisabled={isSuspended}
													options={lxwEditionOptions}
													defaultValue={[
														makeEditionMenuOption(
															this.props
																.activeEditionPreview
																.editionAfterPreviewExpires
														),
													]}
													handler={this.handleChange}
												/>
											</div>
										)}
									<Divider style={{ margin: "12px 0" }} />
									<SimpleSwitch
										label="Litera Document Compare"
										labelVariant="header-5"
										disabled={isSuspended}
										switchValue={
											this.state
												.literaDocumentCompareEnabled ||
											false
										}
										handleSwitchValueChange={() =>
											this.handleChange(
												"literaDocumentCompareEnabled",
												!this.state
													.literaDocumentCompareEnabled
											)
										}
									/>
									<Divider />
									<SimpleSwitch
										label="Inline Document Compare"
										labelVariant="header-5"
										disabled={isSuspended}
										switchValue={
											this.state
												.inlineDocumentCompareEnabled ||
											false
										}
										handleSwitchValueChange={() =>
											this.handleChange(
												"inlineDocumentCompareEnabled",
												!this.state
													.inlineDocumentCompareEnabled
											)
										}
									/>
									<Divider />
									<SimpleSwitch
										label="Automatic OCR"
										labelVariant="header-5"
										disabled={isSuspended}
										switchValue={
											this.state.ocrEnabled || false
										}
										handleSwitchValueChange={() =>
											this.handleChange(
												"ocrEnabled",
												!this.state.ocrEnabled
											)
										}
									/>
									<Divider />
									<div
										style={{
											paddingTop: 10,
										}}
									>
										<LabeledDropDownSelect
											key="ocrPerMonthBaseline"
											label="OCR Operations Per Month Baseline"
											name="ocrPerMonthBaseline"
											placeholder="OCR Operations per Month"
											isAsync
											limitNumber={100}
											options={ocrPerMonthOptions}
											isDisabled={
												!this.state.ocrEnabled ||
												isSuspended
											}
											defaultValue={
												this.props.editMode &&
												this.props.usageLimits
													? [
															makeOCRPerMonthBaselineMenuOption(
																this.props
																	.usageLimits
																	.OCR_PER_MONTH_BASELINE
															),
														]
													: [
															makeOCRPerMonthBaselineMenuOption(
																this.props
																	.newFirmDefaultOCRPerMonthBaseline
															),
														]
											}
											handler={this.handleChange}
										/>
										<Divider />
									</div>
									<SimpleSwitch
										label="Require MFA for all users"
										labelVariant="header-5"
										disabled={isSuspended}
										switchValue={
											this.state
												.mfaIsRequiredForAllUsers ||
											false
										}
										handleSwitchValueChange={() =>
											this.handleChange(
												"mfaIsRequiredForAllUsers",
												!this.state
													.mfaIsRequiredForAllUsers
											)
										}
									/>
									<Divider />
									<SimpleSwitch
										label="Archive Enabled"
										labelVariant="header-5"
										disabled={this.determineIfArchiveSwitchIsDisabled()}
										disabledHoverText={this.getArchiveSwitchDisabledText()}
										switchValue={
											this.state.archiveEnabled === 1
										}
										handleSwitchValueChange={() =>
											this.handleChange(
												"archiveEnabled",
												this.state.archiveEnabled === 1
													? 0
													: 1
											)
										}
									/>
									<Divider />
								</div>
								<div
									style={{
										width: "45vw",
										paddingLeft: "30px",
										paddingRight: "30px",
									}}
								>
									<h4 key="storageLimitsHeader">
										Storage Limits
									</h4>

									<div
										style={{
											display: "flex",
											flexDirection: "row",
											gap: 50,
										}}
									>
										<div style={{ flex: 1 }}>
											<LabeledDropDownSelect
												key="storageLimitGB"
												label="Standard Storage Limit"
												name="storageLimitGB"
												placeholder="Storage Limit"
												isAsync
												isDisabled={isSuspended}
												limitNumber={100}
												options={
													standardStorageLimitOptions
												}
												defaultValue={
													this.props.editMode &&
													this.props.usageLimits
														? [
																makeDataUsageMenuOption(
																	this.props
																		.usageLimits
																		.STORAGE_LIMIT_GB
																),
															]
														: [
																makeDataUsageMenuOption(
																	this.props
																		.newFirmDefaultStorageLimitGB
																),
															]
												}
												handler={this.handleChange}
											/>
											{this.props.editMode && (
												<div>
													Current Standard Usage:{" "}
													{
														this.props.tenantUsage
															.standardSizeHuman
													}
												</div>
											)}
										</div>
										<div style={{ flex: 1 }}>
											<LabeledDropDownSelect
												key="archiveStorageLimitGB"
												label="Archive Storage Limit"
												name="archiveStorageLimitGB"
												placeholder="Archive Storage Limit"
												isAsync
												isDisabled={isSuspended}
												limitNumber={100}
												options={
													archiveStorageLimitOptions
												}
												defaultValue={
													this.props.editMode &&
													this.props.usageLimits
														? [
																makeDataUsageMenuOption(
																	this.props
																		.usageLimits
																		.ARCHIVE_STORAGE_LIMIT_GB
																),
															]
														: [
																makeDataUsageMenuOption(
																	this.props
																		.newFirmDefaultArchiveStorageLimitGB
																),
															]
												}
												handler={this.handleChange}
											/>
											{this.props.editMode && (
												<div>
													Current Archive Usage:{" "}
													{
														this.props.tenantUsage
															.archivedSizeHuman
													}
												</div>
											)}
										</div>
									</div>
									{this.props.editMode && (
										<div
											style={{
												marginTop: "8px",
												color: "grey",
											}}
										>
											Storage Usage Last Calculated at:{" "}
											{parseAndReturnDataUsageString(
												this.props.tenantUsage
													.dataUsageCalculationTime
											)}
										</div>
									)}
								</div>
							</div>
						}
					/>
				</>
			);
		}
	}
}
const mapStateToProps = (state, ownProps) => {
	var newFirmEditionCode = ownProps.match.params.newFirmEditionCode;
	var newFirmDefaultStorageLimitGB,
		newFirmDefaultArchiveStorageLimitGB,
		newFirmDefaultOCREnabled,
		newFirmDefaultOCRPerMonthBaseline,
		newFirmDefaultInlineDocumentCompare,
		newFirmDefaultLiteraDocumentCompare,
		newFirmDefaultMfaIsRequiredForAllUsers,
		newFirmDefaultArchiveEnabled;
	if (newFirmEditionCode) {
		const { defaultValues } =
			lexWorkplaceEditions.options[newFirmEditionCode];
		newFirmDefaultStorageLimitGB = defaultValues.limits.STORAGE_LIMIT_GB;
		newFirmDefaultArchiveStorageLimitGB =
			defaultValues.limits.ARCHIVE_STORAGE_LIMIT_GB;
		newFirmDefaultOCRPerMonthBaseline =
			defaultValues.limits.OCR_PER_MONTH_BASELINE;
		newFirmDefaultOCREnabled = defaultValues.features.automaticOCR;
		newFirmDefaultLiteraDocumentCompare =
			defaultValues.features.literaDocumentCompareEnabled;
		newFirmDefaultInlineDocumentCompare =
			defaultValues.features.inlineDocumentCompareEnabled;
		newFirmDefaultMfaIsRequiredForAllUsers =
			defaultValues.features.mfaIsRequiredForAllUsers;
		newFirmDefaultArchiveEnabled = defaultValues.features.archiveEnabled;
	}

	return {
		availableMatterTags: state.matter.availableMatterTags,
		availableMatterTypes: state.matter.availableMatterTypes,
		availableAssignedStaff: state.matter.availableAssignedStaff,
		availableOfficeBranches: state.matter.availableOfficeBranches,
		availableClients: state.matter.availableClients,
		matterDetails: state.matter.matterDetails,
		clientDetails: state.client.clientDetails,
		firmDetails: state.admin.firmDetails,
		tenantRegion: state.admin.tenantRegion,
		usageLimits: state.admin.usageLimits,
		tenantFeatures: state.admin.tenantFeatures,
		activeEditionPreview: state.admin.activeEditionPreview,
		loading: state.admin.newEditTenantLoading,
		failedCreateAttemptDuplicateID:
			state.admin.failedCreateAttemptDuplicateID,
		navigateAwayFromCreatePage: state.admin.navigateAwayFromCreatePage,
		newFirmEditionCode,
		newFirmDefaultStorageLimitGB,
		newFirmDefaultArchiveStorageLimitGB,
		newFirmDefaultOCREnabled,
		newFirmDefaultOCRPerMonthBaseline,
		newFirmDefaultLiteraDocumentCompare,
		newFirmDefaultInlineDocumentCompare,
		newFirmDefaultMfaIsRequiredForAllUsers,
		newFirmDefaultArchiveEnabled,
		tenantUsage: state.admin.tenantUsage,
		archivedItemsInfo: state.admin.archivedItemsInfo,
	};
};

export default connect(mapStateToProps, {
	getEverythingForNewTenant,
	getEverythingForEditTenant,
	editTenant,
	createTenant,
	unmountNewEditTenant,
	checkIfTenantExists,
})(NewTenant);
